import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { ConfigurationSection, SpaceBetweenRow } from '../StyleComponents';
import {
  sendTeamsTestNotificationRequest,
  TeamsTestNotificationResult
} from '@features/settings/services/UserService';

const MESSAGES: Record<TeamsTestNotificationResult | 'ERROR', string> = {
  SENT: 'settings.teams-test-sent',
  TOO_SOON: 'settings.teams-test-too-soon',
  NO_CHAT: 'settings.teams-test-no-chat',
  DISABLED: 'settings.teams-test-disabled',
  FAILED: 'settings.teams-test-failed',
  ERROR: 'settings.teams-test-error',
};

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Sends the signed-in user a test message from the Team Today app in Teams, so they can check
 * Teams notifications reach them. The API allows one an hour.
 */
export function TeamsTestNotificationSection() {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<TeamsTestNotificationResult | 'ERROR' | null>(null);
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();
  const [waiting, setWaiting] = useState(false);

  // Keeps the button off until another test is allowed
  useEffect(() => {
    const remaining = nextAllowedAt ? new Date(nextAllowedAt).getTime() - Date.now() : 0;
    if (remaining <= 0) {
      setWaiting(false);
      return undefined;
    }
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), Math.min(remaining, ONE_HOUR_MS));
    return () => clearTimeout(timer);
  }, [nextAllowedAt]);

  const send = async () => {
    setSending(true);
    try {
      const response = await sendTeamsTestNotificationRequest();
      setResult(response.result);
      setNextAllowedAt(response.nextAllowedAt);
    } catch {
      setResult('ERROR');
    } finally {
      setSending(false);
    }
  };

  const time = nextAllowedAt ? moment(nextAllowedAt).format('LT') : '';

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.teams-test')}</BodyLarge>
        <OutlineButton dataTest="personalPreferences-teamsTest-button"
                       text="settings.teams-test-button"
                       size={ButtonSize.Small}
                       disabled={sending || waiting}
                       click={send} />
      </SpaceBetweenRow>
      <BodyRegular>{t('settings.teams-test-desc')}</BodyRegular>
      {result && <BodyRegular role="status" weight={600} style={{marginTop: 8}}>
        {t(MESSAGES[result], { time })}
      </BodyRegular>}
    </ConfigurationSection>
  );
}
